import { Box, Skeleton, Stack, Tooltip, Typography } from '@mui/material'
import { FeatureFlagIdBulkActionsV2, FeatureFlagIdGrantReasons, FeatureFlagIdGrantSources } from 'c1gen/features'
import { BulkactionV1BulkActionServiceCreateBulkAction, TaskV1TaskServiceCreateRevokeTask } from 'c1gen/permissions'
import { C1Button } from 'c1ui'
import { BulkActionWrapperDialogProps } from 'components/core/bulk-actions/BulkActionDialog'
import { BulkActionDropdown, BulkActionOption } from 'components/core/bulk-actions/BulkActionDropdown'
import { BulkCreateRevokeTasksDialog } from 'components/core/bulk-actions/BulkCreateRevokeTasksDialog'
import { BulkEditGrantDurationDialog } from 'components/core/bulk-actions/BulkEditGrantDurationDialog'
import { BulkRemoveGrantDurationDialog } from 'components/core/bulk-actions/BulkRemoveGrantDurationDialog'
import { DynamicHeightSizer } from 'components/core/dynamic-height/DynamicHeightSizer'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { APIError } from 'components/core/errors/api'
import { C1Icon } from 'components/core/icons/C1Icon'
import List from 'components/core/list'
import { C1TableRedux, C1TableReduxColumn } from 'components/core/list/C1Table/C1TableRedux'
import { AppUserFilter } from 'components/core/search-filter/appUser/queryRedux'
import { QueryFilter } from 'components/core/search-filter/query'
import { TaskViewLinkV2 } from 'components/core/task-list-v2/task-list-v2-components/TaskViewLinkV2'
import { ExpiresInTimeChip, RelativeTimestamp } from 'components/core/time/display/index'
import { HasCapability, useFeatureChecker, usePermissionChecker } from 'components/no-ui/capability'
import { AppUserDisplayName } from 'components/pages/app/users/app-user-display-name'
import { C1EntitlementDisplayNameWithFetch } from 'components/pages/entitlement/display-name'
import { GrantOrigin } from 'components/pages/entitlement/grant-origin/GrantOrigin'
import { RevokeGrantRequestDialog } from 'components/pages/entitlement/users/list/revokeGrantRequestDialog'
import { ExportData } from 'components/pages/explore/query/ExportData'
import { RevokeTaskType } from 'components/pages/task/type'
import { UserDisplayName } from 'components/pages/user/user-display-name'
import { useIsAppOwner } from 'hooks/useIsAppOwner'
import { AppEntitlementValue } from 'lib/actions/apps/app-entitlement'
import { TaskValue } from 'lib/actions/task'
import { isC1ManagedApp, isCatalogResourceType } from 'lib/constants/system'
import { useSimpleModal } from 'lib/modals'
import { useInitialFilters, useSearch } from 'lib/search'
import { getRefString } from 'lib/websocket-notify'
import { Info } from 'lucide-react'
import {
    AppEntitlementWithExpired,
    SearchAppEntitlementsWithExpiredRequest,
} from 'pbts/c1/api/app/v1/app_entitlement'
import { AppEntitlementUserBindingRef } from 'pbts/c1/api/app/v1/app_refs'
import { AppUserRef } from 'pbts/c1/api/app/v1/app_user'
import { ExportDataRequest, QueryId } from 'pbts/c1/api/exportdata/v1/exportdata'
import { TaskSearchServiceSearchTasksByGrantsRequest } from 'pbts/c1/api/task/v1/search'
import { Timestamp } from 'pbts/google/protobuf/timestamp'
import { FC, ReactNode, useCallback, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { searchAppEntitlementsWithExpiredApi } from 'redux_toolkit/entitlements'
import { tasksSearchByGrantsApi } from 'redux_toolkit/tasks'

type AppEntitlementWithExpiredAndTask = AppEntitlementWithExpired & {
    task?: TaskValue
}

type ExtraProps = {
    openRevokeDialog: (row: AppEntitlementWithExpiredAndTask) => void
    shouldRenderRevokeButton: boolean
}

const cols: C1TableReduxColumn<AppEntitlementWithExpiredAndTask, ExtraProps>[] = [
    {
        field: 'account',
        flex: 2,
        label: <FormattedMessage id="components_app_entitlement_user_list_app_user" defaultMessage="Account" />,
        Component: ({ row: appEntitlementUserValue }) => (
            <AppUserDisplayName appUser={appEntitlementUserValue.appUser} showTooltip showAllUserStatus />
        ),
    },
    {
        field: 'user',
        flex: 2,
        label: <FormattedMessage id="components_app_entitlement_user_list_identity_user" defaultMessage="User" />,
        Component: ({ row: appEntitlementUserValue }) => (
            <>
                {appEntitlementUserValue.user && (
                    <UserDisplayName user={appEntitlementUserValue.user} includeDetails={false} showTooltip />
                )}
                {!appEntitlementUserValue.user && (
                    <Typography variant="unconfigured">
                        <FormattedMessage id="components_app_entitlement_user_list_no_c1_user" defaultMessage="Not assigned" />
                    </Typography>
                )}
            </>
        ),
    },
    {
        field: 'expires',
        label: <FormattedMessage id="components_app_entitlement_user_list_expires_in" defaultMessage="Expires in" />,
        Component: ({ row: appEntitlementUserValue }) => (
            appEntitlementUserValue.expired ? <ExpiresInTimeChip timestamp={appEntitlementUserValue.expired} /> : <Typography variant="unconfigured">
                <FormattedMessage id="components_user_list_no_expiration" defaultMessage="No expiration" />
            </Typography>
        ),
    },
    {
        field: 'discovered',
        label: <FormattedMessage id="components_app_entitlement_user_list_discovered_on" defaultMessage="Discovered on" />,
        Component: ({ row: appEntitlementUserValue }) => (
            <RelativeTimestamp
                date={
                    appEntitlementUserValue.discovered
                        ? Timestamp.toDate(appEntitlementUserValue.discovered)
                        : new Date()
                }
                variant="date"
                typographyVariant="body1"
            />
        ),
    },
    {
        field: 'origins',
        shouldRender: (hasFeature) => hasFeature(FeatureFlagIdGrantReasons) || false,
        Component: ({ row: appEntitlementUserValue }) => (
            <GrantOrigin
                appId={appEntitlementUserValue.appId}
                appEntitlementId={appEntitlementUserValue.appEntitlementId}
                appUserId={appEntitlementUserValue.appUserId}
            />
        ),
        maxWidth: '100px',
    },
    {
        field: 'sources',
        shouldRender: (hasFeature) => hasFeature(FeatureFlagIdGrantSources) || false,
        label: <FormattedMessage id="components_app_entitlement_user_list_sources" defaultMessage="Inherited from" />,
        Component: ({ row: appEntitlementUserValue }) => <Stack direction="column" spacing={2} px={2}>
            {
                appEntitlementUserValue.grantSources && appEntitlementUserValue.grantSources.length > 0
                    ? appEntitlementUserValue.grantSources.map((source, _) => <C1EntitlementDisplayNameWithFetch
                        key={source.id}
                        entitlementId={source.id}
                        appId={source.appId}
                    />)
                    : <C1EmptyIndicator />
            }
        </Stack>,
    },
    {
        field: 'actions',
        isSticky: () => true,
        shouldRender: (_, hasPermissions, __, extras) => (
            (extras.shouldRenderRevokeButton && hasPermissions(TaskV1TaskServiceCreateRevokeTask)) || false
        ),
        Component: ({ row: appEntitlementUserValue, extras }) => <RevokeOrTaskButton
            appEntitlementWithTask={appEntitlementUserValue}
            onClick={(): void => {
                extras.openRevokeDialog(appEntitlementUserValue)
            }}
            isInherited={appEntitlementUserValue.grantSources && appEntitlementUserValue.grantSources.length > 0}
        />,
    },
]

export const RevokeOrTaskButton = <T extends { task?: TaskValue }>({
    appEntitlementWithTask,
    onClick,
    disabled = false,
    isInherited = false,
}: {
    appEntitlementWithTask: T
    onClick: () => void
    disabled?: boolean
    isInherited?: boolean
}): ReactNode => {
    // Hide the revoke button for inherited grants
    if (isInherited) {
        return null
    }

    return <HasCapability permission={TaskV1TaskServiceCreateRevokeTask}>
        {
            appEntitlementWithTask.task ? <Stack spacing={1} direction="row" alignItems="center">
                <Tooltip title={<FormattedMessage id="components_app_user_list_revoke_task" defaultMessage="A pending revocation task exists for this grant." />}>
                    <C1Icon icon={Info} />
                </Tooltip>
                <TaskViewLinkV2 task={appEntitlementWithTask.task} />
            </Stack>
                : <Tooltip title={disabled ? <FormattedMessage id="tooltip_revoke_disabled" defaultMessage="You don't have permission to revoke this access." /> : undefined}>
                    <Box>
                        <C1Button
                            color="info"
                            variant="outlined"
                            onClick={onClick}
                            disabled={disabled}
                            sx={{
                                mx: 1,
                            }}
                        >
                            <FormattedMessage id="components_app_user_list_revoke_access" defaultMessage="Revoke" />
                        </C1Button>
                    </Box>
                </Tooltip>
        }
    </HasCapability>
}

type AppEntitlementUserValueListProps = {
    appEntitlement: AppEntitlementValue
    skipSerializeURLParams?: boolean
}

const urlEncodeAppUserRef: (k: string, v: unknown) => string | string[] = (key, value) => (
    key === 'appUserRefs' && Array.isArray(value)
        ? value.map((v) => getRefString(v))
        : value as string
)

export const AppEntitlementUserValueList: FC<AppEntitlementUserValueListProps> = ({
    appEntitlement,
    skipSerializeURLParams = false,
}) => {
    const [userIsOwner, isAppOwnerLoading, appOwnerError] = useIsAppOwner(appEntitlement.appId)
    const hasPermission = usePermissionChecker()
    const { open: openRevokeModal } = useSimpleModal()

    const hasBulkActionsV2 = useFeatureChecker(FeatureFlagIdBulkActionsV2)
    const hasGrantSources = useFeatureChecker(FeatureFlagIdGrantSources)

    const canCreateBulkAction = hasPermission(BulkactionV1BulkActionServiceCreateBulkAction)

    const initialFilters = useInitialFilters(
        SearchAppEntitlementsWithExpiredRequest,
        [],
        undefined,
        undefined,
        ['appUserRefs'],
    )

    const emptyReq = useMemo<Partial<SearchAppEntitlementsWithExpiredRequest>>(
        () => (skipSerializeURLParams
            ? SearchAppEntitlementsWithExpiredRequest.create({
                appId: appEntitlement.appId,
                appEntitlementId: appEntitlement.id,
            })
            : { appId: appEntitlement.appId, appEntitlementId: appEntitlement.id }
        ),
        [appEntitlement.appId, appEntitlement.id, skipSerializeURLParams],
    )

    const [filter, setFilter] = useState<SearchAppEntitlementsWithExpiredRequest>(
        SearchAppEntitlementsWithExpiredRequest.create({
            ...initialFilters,
            ...emptyReq,
        }),
    )

    const { canClear, clear, onQueryChange, onChange } = useSearch(
        SearchAppEntitlementsWithExpiredRequest,
        filter,
        setFilter,
        emptyReq,
        urlEncodeAppUserRef,
    )

    const openRevokeDialog = useCallback((aeu: AppEntitlementWithExpired): void => {
        openRevokeModal(RevokeGrantRequestDialog, {
            appUser: aeu.appUser,
            appEntitlement,
        })()
    }, [appEntitlement, openRevokeModal])

    let filteredCols = isC1ManagedApp(appEntitlement.appId) &&
        (appEntitlement.isAutomationEnabled || isCatalogResourceType(appEntitlement.appResourceType?.id || ''))
        ? cols.filter((col) => col.field !== 'actions')
        : cols

    filteredCols = hasGrantSources
        ? filteredCols
        : filteredCols.filter((col) => col.field !== 'sources')

    const options: BulkActionOption<AppEntitlementWithExpiredAndTask, object>[] =
        useMemo(() => [
            ...(userIsOwner ? [{
                key: 'createRevokeTask',
                label: <FormattedMessage id="bulk_action_create_revoke_task" defaultMessage="Revoke" />,
                behavior: {
                    type: 'dialog' as const,
                    Dialog: BulkCreateRevokeTasksDialog<AppEntitlementWithExpiredAndTask>(),
                },
            },
            {
                key: 'editGrantDuration',
                label: <FormattedMessage id="bulk_action_edit_grant_duration" defaultMessage="Set expiration" />,
                behavior: {
                    type: 'dialog' as const,
                    Dialog: BulkEditGrantDurationDialog as FC<BulkActionWrapperDialogProps<AppEntitlementWithExpiredAndTask, object>>,
                },
            },
            {
                key: 'removeGrantDuration',
                label: <FormattedMessage id="bulk_action_remove_grant_duration" defaultMessage="Remove expiration" />,
                behavior: {
                    type: 'dialog' as const,
                    Dialog: BulkRemoveGrantDurationDialog as FC<BulkActionWrapperDialogProps<AppEntitlementWithExpiredAndTask, object>>,
                },
            }] : []),
        ], [userIsOwner])

    const [selections, setSelections] = useState<AppEntitlementWithExpired[]>([])

    const [page, setPage] = useState<number>(0)

    const {
        data: entitlementData, isError, isLoading, error, canLoadMore, shouldInvalidateList, getPage,
    } = searchAppEntitlementsWithExpiredApi.useSearchAppEntitlementsWithExpired(filter, {
        notify: true,
        disableEssentialTableExistenceCheck: true,
    })

    const onPageChange = useCallback(
        async (n: number) => {
            await getPage(n)
            setPage(n)
        },
        [getPage],
    )

    const {
        data: taskData,
    } = tasksSearchByGrantsApi.useSearchTasksByGrants(TaskSearchServiceSearchTasksByGrantsRequest.create({
        taskType: RevokeTaskType,
        appEntitlementUserBindingRefs: entitlementData[page] ? entitlementData[page].map((v) => AppEntitlementUserBindingRef.create({
            appId: v.appId,
            appEntitlementId: v.appEntitlementId,
            appUserId: v.appUserId,
        })) : [],
    }), {
        notify: true,
        autoReload: true,
        actionable: entitlementData[page] && entitlementData[page].length > 0,
    })

    const appUserTaskMap = useMemo(() => taskData[0]?.reduce((acc, v) => {
        if (v.type?.taskType.oneofKind !== 'revoke') return acc
        const appUserId = v.type.taskType.revoke.appUserId
        const appId = v.type.taskType.revoke.appId
        const appEntitlementId = v.type.taskType.revoke.appEntitlementId

        acc.set(`${appUserId}:${appId}:${appEntitlementId}`, v)
        return acc
    }, new Map<string, TaskValue>()), [taskData])

    const mergedData = useMemo(() => entitlementData.map((p, i) => (i === page
        ? p.map((v) => {
            const task = appUserTaskMap?.get(`${v.appUserId}:${v.appId}:${v.appEntitlementId}`)
            return task ? { ...v, task } : v
        })
        : p
    )), [entitlementData, appUserTaskMap, page])

    const onReload = searchAppEntitlementsWithExpiredApi.useInvalidateSearchAppEntitlementsWithExpired()
    const table = useMemo(() => <C1TableRedux<AppEntitlementWithExpiredAndTask, ExtraProps>
        data={mergedData}
        isError={isError}
        isLoading={isLoading}
        error={error}
        canLoadMore={canLoadMore}
        cols={filteredCols}
        onPageChange={onPageChange}
        page={page}
        shouldReload={shouldInvalidateList}
        onReload={onReload}
        staticExtraProps={{
            openRevokeDialog,
            shouldRenderRevokeButton: userIsOwner,
        }}
        getId={(aewe): string => `${aewe.appId}:${aewe.appEntitlementId}:${aewe.appUserId}`}
        extraFooterActions={hasBulkActionsV2 && canCreateBulkAction
            ? <BulkActionDropdown<AppEntitlementWithExpiredAndTask, object>
                selections={selections}
                setSelections={setSelections}
                options={options}
                extraProps={appEntitlement}
            />
            : undefined
        }
        setSelections={setSelections}
        selections={selections}
    />, [
        mergedData, isError, isLoading, error, canLoadMore, filteredCols, onPageChange,
        page, shouldInvalidateList, openRevokeDialog, hasBulkActionsV2, selections, options,
        appEntitlement, onReload, canCreateBulkAction, userIsOwner,
    ])

    const exportDataActionRequest = ExportDataRequest.create({
        queryId: QueryId.ENTITLEMENT_GRANTS,
        request: {
            oneofKind: 'searchAppEntitlementsWithExpiredRequest',
            searchAppEntitlementsWithExpiredRequest: filter,
        },
    })

    const search = <Stack direction="row" flexGrow={1}>
        <Stack direction="row" spacing={1}>
            <QueryFilter
                autoFocus
                query={filter?.query || ''}
                setQuery={(q): void => onQueryChange(q as unknown as string, false)}
                onChange={onQueryChange}
            />

            <AppUserFilter
                selected={filter.appUserRefs}
                onChange={(appUserRefs): void => onChange({ appUserRefs: appUserRefs.map((v) => AppUserRef.create(v)) })}
                appId={appEntitlement.appId}
            />

            {canClear && (
                <C1Button onClick={clear} variant="text" color="primary">
                    <FormattedMessage id="components_task_search_index_clear" defaultMessage="Clear" />
                </C1Button>
            )}
        </Stack>

        <Box ml="auto">
            <ExportData exportDataRequest={exportDataActionRequest} />
        </Box>
    </Stack>

    if (isAppOwnerLoading) {
        return <Skeleton />
    }

    return <DynamicHeightSizer>
        {appOwnerError && <APIError err={appOwnerError} />}
        <List<AppEntitlementWithExpired> search={search} table={table} />
    </DynamicHeightSizer>
}
